// CRM-iframe outbound reporter.
//
// Mirror of crmReadReporter, but for OUR side of the conversation. The CRM's
// chat button shows the read status of our last outgoing message (✓ sent /
// ✓✓ read by the lead); telegram-tt's live session knows both the instant we
// send and the instant the peer reads, so we forward them to the parent SPA
// instead of waiting for the worker's account-health sweep (which by design
// yields to this very iframe and so lags by minutes).
//
// Two apiUpdate signals, both 1:1 user DMs only:
//   updateMessageSendSucceeded → we just sent a message → tg:messageSent
//   updateThreadReadState (with lastReadOutboxMessageId) → the peer read our
//     outgoing history up to maxId → tg:outboxRead
// Both are best-effort fast paths; the worker's getDifference sweep
// (bumpContactOutbound / bumpContactOutboundRead) stays the authoritative
// backstop and converges with these via the monotonic-max watermarks.
//
// Lives in its own module (not crmBridge.ts) because crmBridge is imported by
// the gramjs worker too, where there is no global store / window. This file is
// imported only from index.tsx on the main thread.

import { getGlobal, addActionHandler } from '../global';
import { selectCurrentMessageList } from '../global/selectors';
import { selectThreadReadState } from '../global/selectors/threads';
import { addCallback } from '../lib/teact/teactn';

import { getCrmAccountId, getCrmOrigin, isCrmIframeMode } from './crmBridge';
import { isUserId } from './entities/ids';

let isRegistered = false;
// Per-chat last reported outbox-read marker, so we only post on a forward jump
// (the genuine "peer read further" event), not on every state tick.
const lastReportedOutboxByChat = new Map<string, number>();

function postToCrm(payload: object) {
  const origin = getCrmOrigin();
  if (!origin || !window.parent || window.parent === window) return;
  try {
    window.parent.postMessage(payload, origin);
  } catch {
    // Best effort — never block the update pipeline on telemetry.
  }
}

export function registerCrmOutboxReporter() {
  if (isRegistered || !isCrmIframeMode()) return;
  isRegistered = true;

  // ── ✓ sent ──────────────────────────────────────────────────────────
  // updateMessageSendSucceeded is a discrete, reliably-caught apiUpdate: the
  // instant we send, the server-confirmed id arrives exactly once. Event-based
  // is the right shape here.
  addActionHandler('apiUpdate', (global, actions, update) => {
    // Best-effort: a throw here propagates into teactn's action drain
    // (try/finally with no catch) and wipes the queue, breaking other
    // handlers in the tick — so swallow everything.
    try {
      if (update['@type'] === 'updateMessageSendSucceeded') {
        const { chatId, message } = update;
        if (chatId && isUserId(chatId) && chatId !== global.currentUserId) {
          postToCrm({
            type: 'tg:messageSent',
            payload: { accountId: getCrmAccountId(), peerId: chatId, messageId: message.id },
          });
        }
      }
    } catch {
      // never disrupt the update pipeline
    }
  });

  // ── ✓✓ read ─────────────────────────────────────────────────────────
  // The peer's outbox-read marker. We DELIBERATELY diff the current chat's
  // read STATE on every teactn tick (like crmReadReporter does for inbox
  // reads) instead of listening for the one-shot updateThreadReadState
  // apiUpdate: that event is easily missed (coalesced / delivered to another
  // session / dropped by a throwing handler upstream), which left ✓✓
  // permanently lagging. Reading the current state value is self-healing —
  // whatever telegram-tt currently knows the peer has read gets reported the
  // next tick, and on every chat open.
  addCallback((global: ReturnType<typeof getGlobal>) => {
    try {
      const current = selectCurrentMessageList(global);
      const chatId = current?.chatId;
      // 1:1 user DMs only (the chat button's ✓✓ tracks lead conversations).
      if (!chatId || !isUserId(chatId) || chatId === global.currentUserId) return;

      const maxId = selectThreadReadState(global, chatId, current.threadId)?.lastReadOutboxMessageId;
      if (!maxId) return;

      const prev = lastReportedOutboxByChat.get(chatId);
      if (prev !== undefined && maxId <= prev) return;
      lastReportedOutboxByChat.set(chatId, maxId);

      postToCrm({
        type: 'tg:outboxRead',
        payload: { accountId: getCrmAccountId(), peerId: chatId, maxId },
      });
    } catch {
      // never disrupt the state-callback pipeline
    }
  });
}
